// Shared editing helpers for Lite's BOM pages. Both the right-panel
// (workspace/rightPanel/BomPanel.tsx) and the Complete BOM page
// (bomPage/CompleteBomPage.tsx) hold their own copy of BomEditState in
// component state and reuse these pure helpers to render rows / dropdowns
// and to build the SAVE_LITE_BOM_REQUEST payload, so the two views stay in
// sync without one taking a dependency on the other.

import { AttachmentType } from 'projectDesign/components/projectConfiguration/fields/attachmentType';
import {
  isNxtHorizon,
  isSolarMount,
  isCommercialProduct,
  isRmGridflex10D,
  isRmGridflex5Rwdi,
  isRM10Evolution,
  isRMGridFlex,
} from '__common/constants/products';
import { disableMandatoryMidSupportCondition } from 'projectDesign/components/projectConfiguration/utils/updateModuleDims';
import { sortByUniracRMRequest, sortByUniracSMRequest } from 'bom/components/bomTable/utils/bomTableSorting';
import type { bomPart } from '../../../../decls/bom/parts';
export type BomPart = bomPart;

// Per-component buffer of user edits. Held as strings (not numbers) for qty
// fields so the controlled inputs can be momentarily empty mid-edit instead
// of snapping to "0" on backspace.
export type BomEditState = {
  rowFinish: { [partNumber: string]: string };
  partQty: { [partNumber: string]: string };
  addedAccessories: { [partNumber: string]: boolean };
  accessoryQty: { [partNumber: string]: string };
  discountPct: string | undefined;
};

export const initialBomEditState = (): BomEditState => ({
  rowFinish: {},
  partQty: {},
  addedAccessories: {},
  accessoryQty: {},
  discountPct: undefined,
});

const bomEditCache: { [projectId: string]: BomEditState } = {};

const cloneBomEditState = (s: BomEditState): BomEditState => ({
  rowFinish: { ...s.rowFinish },
  partQty: { ...s.partQty },
  addedAccessories: { ...s.addedAccessories },
  accessoryQty: { ...s.accessoryQty },
  discountPct: s.discountPct,
});

export const cacheBomEditState = (projectId: string | undefined | null, s: BomEditState): void => {
  if (!projectId) return;
  bomEditCache[projectId] = cloneBomEditState(s);
};

export const readBomEditCache = (projectId: string | undefined | null): BomEditState | undefined => {
  if (!projectId) return undefined;
  const cached = bomEditCache[projectId];
  return cached ? cloneBomEditState(cached) : undefined;
};

// Drop cached qty overlays when the layout is discarded (satellite ↔ blank,
// Clear All, BOM_RESET). Matching part numbers would otherwise keep the
// previous array's accessory quantities after Suggested has already updated.
export const clearBomEditCache = (projectId: string | undefined | null): void => {
  if (!projectId) return;
  delete bomEditCache[projectId];
};

// PartType names the BOM marks as roof attachments. Matches every
// 'attach'-typed PartType in the DB so the Attachment section catches
// Comp / Tile / Metal variants too. Without 'Comp Roof Attachment' here,
// NXT-Shingle's STRONGHOLD ATT W/BUTYL falls through to the Part List
// section instead of the Attachment section.
export const ATTACHMENT_TYPES = new Set([
  'Roof Attachment',
  'Comp Roof Attachment',
  'Metal Roof Attachment',
  'Tile Roof Attachment',
  'Attachment',
  'Flashing'
]);

// Same check as Pro BOM: only the project's selected uanchor_part_number
// (a string; .includes matches that one SKU).
export function isSelectedUAnchorPart(
  partNumber: string | undefined,
  uanchorPartNumber?: string | null,
): boolean {
  const uanchorList = uanchorPartNumber || [];
  return !!(partNumber && uanchorList && uanchorList.includes(partNumber));
}

export const FINISHES = ['Mill', 'Clear', 'Dark'];

// Finish detection is description-driven because the backend `color` field
// is inconsistent: Mill parts are sometimes coded 'AL', sometimes 'MILL',
// and sometimes mistakenly 'CLR' (DB data-entry quirk seen in
// backend/ubuilder/ci/ubuilder2.sql). The part description, on the other
// hand, reliably ends with the finish token ("SM RAIL 168\" MILL", "...
// CLR", "... DRK"), so we sniff that first and only fall back to color.
const FINISH_TOKENS: { [label: string]: string[] } = {
  Mill: ['MILL', 'MIL'],
  Clear: ['CLR', 'CLEAR'],
  Dark: ['DRK', 'DARK'],
};
export const FINISH_COLOR_CODES: { [label: string]: string[] } = {
  Mill: ['AL', 'MILL', 'MIL'],
  Clear: ['CLR', 'CLEAR'],
  Dark: ['DRK', 'DARK'],
};

export function getDescriptionFinish(desc: string): string | null {
  let d = (desc || '').toUpperCase();
  if (d.endsWith('.')) {
    d = d.slice(0, -1);
  }
  for (const finish of Object.keys(FINISH_TOKENS)) {
    const tokens = FINISH_TOKENS[finish];
    if (tokens.some(t => d.endsWith(' ' + t) || d.endsWith(t) || d.includes(' ' + t + ' '))) {
      return finish;
    }
  }
  return null;
}

// If the description tells us a finish, that's the truth — a Mill-described
// variant must NOT match Clear even if its color code is 'CLR'. Falls back
// to color codes only when the description has no finish suffix.
export function variantMatchesFinish(v: BomPart, finish: string): boolean {
  const descFinish = getDescriptionFinish(v.description || '');
  if (descFinish) return descFinish === finish;
  const code = (v.color || '').toUpperCase();
  return (FINISH_COLOR_CODES[finish] || []).includes(code);
}

export const availableFinishes = (part: BomPart): string[] => {
  if (!part.variants || part.variants.length === 0) return [];
  const candidates = [part, ...part.variants];
  return FINISHES.filter(label => candidates.some(v => variantMatchesFinish(v, label)));
};

// Look up a previously-saved finish label for a part by checking the
// color_finish map under every related-part-number key. The map is written
// keyed by every variant's number so that — regardless of which variant the
// backend returns as the "parent" after _create_bom regenerates the BOM
// (e.g. when the autosave PUT's empty BOM POST wipes user_builds) — the
// lookup still resolves the user's selection.
export const lookupSavedFinish = (part: BomPart, extraParameters: any): string | undefined => {
  const map = extraParameters && extraParameters.selections && extraParameters.selections.color_finish;
  if (!map || typeof map !== 'object') return undefined;
  if (map[part.number]) return map[part.number];
  if (part.variants) {
    for (const v of part.variants) {
      if (map[v.number]) return map[v.number];
    }
  }
  return undefined;
};

// Builds a rowFinish dict from extra_parameters.selections.color_finish.
// Used by both BomPanel and CompleteBomPage to seed local state on mount
// and after the BOM partsList lands, so the user's persisted color choice
// survives an empty-BOM-POST regen of user_builds.
export const seedRowFinishFromSelections = (
  partsList: BomPart[],
  extraParameters: any,
): { [partNumber: string]: string } => {
  const seeded: { [partNumber: string]: string } = {};
  (partsList || []).forEach((p) => {
    const saved = lookupSavedFinish(p, extraParameters);
    if (saved) seeded[p.number] = saved;
  });
  return seeded;
};

export const seedAccessoryQtysFromSelections = (
  accessoriesList: BomPart[],
  extraParameters: any,
): { [partNumber: string]: string } => {
  const accessoryQty: { [partNumber: string]: string } = {};
  const quantities = extraParameters && extraParameters.quantities;
  if (!quantities || typeof quantities !== 'object') return accessoryQty;
  (accessoriesList || []).forEach((a) => {
    if (!a) return;
    const entry = quantities[a.number];
    if (!entry || typeof entry !== 'object') return;
    const qty = entry.qty;
    if (qty === undefined || qty === null || qty === '') return;
    accessoryQty[a.number] = String(qty);
  });
  return accessoryQty;
};

// Seed accessory qty from the parts_list quantity for accessories the
// backend returns in BOTH parts_list and accessories_list. Such an item is
// already part of the build, so its authoritative quantity is the one in
// parts_list — which can differ from the catalog's suggested_quantity. On a
// fresh load this makes the accessory's added card show the parts_list qty.
// Callers gate re-seeding on the local addedAccessories map so that a
// remove + re-add clears the override and falls back to the accessories_list
// suggested quantity instead of re-pulling the parts_list value.
export const seedAccessoryQtysFromPartsList = (
  accessoriesList: BomPart[],
  partsList: BomPart[],
): { [partNumber: string]: string } => {
  const seeded: { [partNumber: string]: string } = {};
  const partQtyByNumber: { [partNumber: string]: number } = {};
  (partsList || []).forEach((p) => {
    if (p && p.number) partQtyByNumber[p.number] = p.quantity;
  });
  (accessoriesList || []).forEach((a) => {
    if (!a || !a.number) return;
    const qty = partQtyByNumber[a.number];
    if (qty === undefined || qty === null) return;
    seeded[a.number] = String(qty);
  });
  return seeded;
};

export const finishOf = (part: BomPart, state: BomEditState, extraParameters?: any): string => {
  const stored = state.rowFinish[part.number];
  if (stored) return stored;
  // Persisted selection from a prior save. Survives user_builds wipes
  // because it lives in extra_parameters (carried via update_configuration).
  const saved = lookupSavedFinish(part, extraParameters);
  if (saved) return saved;
  // Derive from the part itself before falling back to alphabetical default.
  // After a BOM POST, the backend persists the user's selected variant in
  // user_builds → next GET returns that variant as the parent (e.g. CCLAMPD1
  // for Dark). On remount, state.rowFinish is empty; without this branch the
  // dropdown would show the first alphabetical option (always Mill) even
  // though the persisted part is Dark, making the user's choice look lost.
  const fromDesc = getDescriptionFinish(part.description || '');
  if (fromDesc) return fromDesc;
  const code = (part.color || '').toUpperCase();
  if (code) {
    for (const finish of Object.keys(FINISH_COLOR_CODES)) {
      if (FINISH_COLOR_CODES[finish].includes(code)) return finish;
    }
  }
  const avail = availableFinishes(part);
  if (avail.length > 0) return avail[0];
  return 'Mill';
};

export const resolveVariant = (part: BomPart, state: BomEditState, extraParameters?: any): BomPart => {
  if (!part.variants || part.variants.length === 0) return part;
  const finish = finishOf(part, state, extraParameters);
  // Variants from the BOM reducer don't always include the base part itself
  // (it's often the parent), so check the part too as a virtual self-variant.
  const candidates = [part, ...part.variants];
  const match = candidates.find(v => variantMatchesFinish(v, finish));
  return match || part;
};

export const partQtyDisplay = (part: BomPart, state: BomEditState): string => {
  const raw = state.partQty[part.number];
  if (raw !== undefined) return raw;
  return String(part.quantity);
};

export const partQtyNumber = (part: BomPart, state: BomEditState): number => {
  const raw = state.partQty[part.number];
  if (raw === undefined) return part.quantity;
  const n = parseInt(raw, 10);
  return isNaN(n) ? 0 : n;
};

export const isAccessoryAdded = (a: BomPart, state: BomEditState): boolean => {
  // Local component state takes precedence — it carries the user's
  // in-flight toggle before the 600ms debounce + POST commits. After
  // unmount/remount (page navigation) the local buffer is gone and we
  // fall back to a.selected, which lives in Redux state.bom.accessoriesList
  // and is kept in sync by SELECT_ACCESSORY / UNSELECT_ACCESSORY
  // dispatches in BomPanel.toggleAccessory and CompleteBomPage.toggleAccessory.
  // The Redux flag survives navigation, so the destination page renders
  // the accessory's added/not-added state correctly.
  const local = state.addedAccessories[a.number];
  return local !== undefined ? local : !!a.selected;
};

export const accessoryQtyNumber = (a: BomPart, state: BomEditState, buildSuggestedQty?: number): number => {
  const raw = state.accessoryQty[a.number];
  if (raw !== undefined) {
    const n = parseInt(raw, 10);
    return isNaN(n) ? 0 : n;
  }
  const suggested = buildSuggestedQty !== undefined ? buildSuggestedQty : a.suggested_quantity;
  return suggested || a.quantity || 0;
};

export const accessoryQtyDisplay = (a: BomPart, state: BomEditState, buildSuggestedQty?: number): string => {
  const raw = state.accessoryQty[a.number];
  if (raw !== undefined) return raw;
  const suggested = buildSuggestedQty !== undefined ? buildSuggestedQty : a.suggested_quantity;
  return String(suggested || a.quantity || 0);
};

// Validates a digits-only string input (or empty). Returns true if the
// candidate is acceptable for storage in the qty buffer.
export const isValidQtyInput = (raw: string): boolean => {
  return raw === '' || /^\d+$/.test(raw);
};

// Mirrors the optgroup structure from the redesign reference HTML. Used by
// the static fallback dropdown when no legacy field config is available.
export const ATTACHMENT_TYPE_GROUPS: Array<{ label: string; options: string[] }> = [
  { label: 'Comp Shingle', options: ['Flashkit PRO', 'Flashloc COMP', 'Butyl'] },
  { label: 'Tile Roof', options: ['Flashkit TR', 'Solarhook', 'Standoff'] },
  { label: 'Metal Roof', options: ['S-5!', 'Solar Connections', 'Butyl (Metal)'] },
  { label: 'Flat Roof', options: ['Flashloc RM', 'Tilt Legs', 'Butyl (Flat)'] },
];

// Maps the right-panel dropdown labels to backend AttachmentType integers.
// "Butyl" / "Butyl (Metal)" are product-aware: NXT picks STRONGHOLD_ATT_BUTYL
// (30) → SHBUTYLM2; SM picks SM_BUTYL_DTD (33) → 004BUTYL*. Other entries
// share the same int across products.
export const attachmentLabelToInt = (label: string, productId?: number): number | undefined => {
  const isNxt = !!productId && isNxtHorizon(productId);
  const isSm = !!productId && isSolarMount(productId);
  switch (label) {
    case 'Flashkit PRO': return AttachmentType.FLASHKIT_PRO;        // 22
    case 'Flashloc COMP': return AttachmentType.FLASHLOC_COMP_KIT;   // 23
    case 'Butyl':
      if (isNxt) return AttachmentType.STRONGHOLD_ATT_BUTYL;              // 30
      if (isSm) return AttachmentType.SM_BUTYL_DTD;                      // 33
      return AttachmentType.BUTYL;                                        // 35
    case 'Butyl (Metal)':
      if (isNxt) return AttachmentType.STRONGHOLD_ATT_BUTYL;
      if (isSm) return AttachmentType.SM_BUTYL_DTD;
      return AttachmentType.BUTYL;
    case 'Butyl (Flat)': return AttachmentType.BUTYL;               // 35
    case 'Flashkit TR': return AttachmentType.FLASHKIT;            // 31
    case 'Solarhook': return AttachmentType.SOLARHOOK_UNIVERSAL; // 15
    case 'Standoff': return AttachmentType.MINI_1SS;            // 16
    case 'S-5!': return AttachmentType.PM_9000S;            // 19
    case 'Solar Connections': return AttachmentType.PM_ADJUST;           // 20
    case 'Flashloc RM': return AttachmentType.UNIRAC_FLASHLOC_RM;  // 1
    case 'Tilt Legs': return AttachmentType.CUSTOM;              // 3
    default: return undefined;
  }
};

// Reverse lookup: given the persisted attachment_type int, find the label
// the static dropdown should display. NXT/SM Butyl ints both map back to
// "Butyl". Falls back to "Butyl" when nothing matches.
export const attachmentIntToLabel = (value: number | undefined, productId?: number): string => {
  if (value === undefined || value === null) return 'Butyl';
  const isNxt = !!productId && isNxtHorizon(productId);
  const isSm = !!productId && isSolarMount(productId);
  if (isNxt && value === AttachmentType.STRONGHOLD_ATT_BUTYL) return 'Butyl';
  if (isSm && value === AttachmentType.SM_BUTYL_DTD) return 'Butyl';
  switch (value) {
    case AttachmentType.FLASHKIT_PRO: return 'Flashkit PRO';
    case AttachmentType.FLASHLOC_COMP_KIT: return 'Flashloc COMP';
    case AttachmentType.BUTYL: return 'Butyl (Flat)';
    case AttachmentType.FLASHKIT: return 'Flashkit TR';
    case AttachmentType.SOLARHOOK_UNIVERSAL: return 'Solarhook';
    case AttachmentType.MINI_1SS: return 'Standoff';
    case AttachmentType.PM_9000S: return 'S-5!';
    case AttachmentType.PM_ADJUST: return 'Solar Connections';
    case AttachmentType.UNIRAC_FLASHLOC_RM: return 'Flashloc RM';
    case AttachmentType.CUSTOM: return 'Tilt Legs';
    default: return 'Butyl';
  }
};

// Builds the SAVE_LITE_BOM_REQUEST body from the local edit state. Both
// pages use this so the POST shape stays identical regardless of where the
// edit happened. The caller is responsible for productId -> productName.
export const buildBomSavePayload = (params: {
  state: BomEditState;
  partsList: BomPart[];
  accessoriesList: BomPart[];
  extraParameters: any;
}): {
  parts_list: any[];
  rails_lengths: any[];
  extra_parameters: any;
} => {
  const { state, partsList, accessoriesList, extraParameters } = params;

  // Set of accessory numbers — used to deduplicate. After a prior
  // accessory add, the backend's FETCH response returns the accessory in
  // BOTH parts_list AND accessories_list. partsPayload must NOT
  // unconditionally re-send accessory items: the user may have just
  // toggled the accessory off, and accessoriesPayload (which gates on
  // isAccessoryAdded) is the authoritative source for which accessories
  // belong in user_builds. Filter accessory-numbered items out of the
  // parts side so accessoriesPayload alone decides their fate.
  const accessoryNumbers = new Set(
    (accessoriesList || []).map(a => a && a.number).filter(Boolean)
  );

  // Resolve every part to its currently-selected variant (so the POST
  // captures the user's finish choice via number/color), then apply the
  // qty override if the user edited it. Skip accessory-numbered items;
  // they're handled by accessoriesPayload below.
  // colorFinishMap is built keyed by every related-part-number (parent +
  // all variants) -> finish label. Persisting under all keys lets the
  // reload-side seedRowFinishFromSelections find the user's choice no
  // matter which variant the backend returns as the "parent" — which
  // matters because the autosave PUT's empty BOM POST can wipe
  // user_builds and force _create_bom to regenerate the default (Mill)
  // parent. Without this, the color choice would be silently lost while
  // the qty edit (mirrored into extra_parameters.quantities) survives.
  const colorFinishMap: { [partNumber: string]: string } = {};
  const partsPayload = (partsList || [])
    .filter(p => p && !accessoryNumbers.has(p.number))
    .map((p) => {
      const v = resolveVariant(p, state, extraParameters);
      if (p.variants && p.variants.length > 0) {
        const finish = finishOf(p, state, extraParameters);
        colorFinishMap[p.number] = finish;
        colorFinishMap[v.number] = finish;
        p.variants.forEach((vv) => { colorFinishMap[vv.number] = finish; });
      }
      return {
        number: v.number,
        color: v.color || '',
        category: v.category,
        quantity: partQtyNumber(p, state),
        suggested_quantity: v.suggested_quantity,
      };
    });

  const accessoriesPayload = (accessoriesList || [])
    .filter(a => isAccessoryAdded(a, state))
    .map(a => ({
      number: a.number,
      color: a.color || '',
      category: a.category,
      quantity: accessoryQtyNumber(a, state),
      suggested_quantity: a.suggested_quantity,
    }));

  const extra = { ...(extraParameters || {}) };
  const discountNum = Math.max(0, Math.min(100, parseInt(state.discountPct, 10) || 0));
  extra.discount_pct = discountNum;

  // Persist color/finish selections in extra_parameters.selections so they
  // survive a user_builds wipe (e.g. the autosave PUT's empty BOM POST
  // when panelCountChanged || regenBom). On reload, BomPanel /
  // CompleteBomPage seed state.rowFinish from this map via
  // seedRowFinishFromSelections so the dropdown reflects the persisted
  // choice even after _create_bom regenerates the default Mill parent.
  const prevSelections = (extra.selections && typeof extra.selections === 'object') ? extra.selections : {};
  const prevColorFinish = (prevSelections.color_finish && typeof prevSelections.color_finish === 'object')
    ? prevSelections.color_finish
    : {};
  // Persist selected accessory numbers so they survive page reload.
  // The backend's GET returns selected:false for all accessories regardless
  // of user_builds, so we store the list here and restore in bomReducer.
  const selectedAccessoryNumbers = (accessoriesPayload || []).map(a => a.number);
  extra.selections = {
    ...prevSelections,
    color_finish: { ...prevColorFinish, ...colorFinishMap },
    accessories: selectedAccessoryNumbers,
  };

  // Rebuild extra_parameters.quantities from the current parts payload so
  // it stays in sync with user_builds. Without this, restorePartQty (in
  // bom/components/utils/loadBomSelectionsQtys.ts) sees a stale quantities
  // map from the prior GET's payload, mutates part.quantity back to the
  // suggested value, and the user's edit appears not to persist after
  // reload — even though user_builds in the DB are correct. Mirrors the
  // legacy bomHelpers.ts:203 rebuild done by the engineering save flow.
  const allPayloadParts = [...partsPayload, ...accessoriesPayload];
  extra.quantities = allPayloadParts.reduce((acc: any, part) => {
    acc[part.number] = { qty: part.quantity, suggestedQty: part.suggested_quantity };
    return acc;
  }, {});

  return {
    parts_list: allPayloadParts,
    rails_lengths: [],
    extra_parameters: extra,
  };
};

export function getAugmentedPartsList(
  partsList: BomPart[],
  productId: number | undefined,
  domesticContent: boolean,
  totalModules: number,
  moduleLength: number = 0,
  snowLoad: number = 0,
): BomPart[] {
  if (!partsList || partsList.length === 0) return partsList;
  if (productId === undefined || productId === null) return partsList;

  let augmented = [...partsList];

  const isMidSupportMandatory = disableMandatoryMidSupportCondition(moduleLength, snowLoad);
  if (isMidSupportMandatory) {
    let midSupportPartNumber: string | undefined;
    let midSupportDescription: string | undefined;
    let midSupportPrice: number | undefined;

    if (isRMGridFlex(productId)) {
      midSupportPartNumber = '360200';
      midSupportDescription = 'MID SUPPORT KIT';
      midSupportPrice = 14.89;
    }

    if (midSupportPartNumber && midSupportDescription && midSupportPrice !== undefined) {
      const hasMidSupport = augmented.some(p => p && p.number === midSupportPartNumber);
      if (!hasMidSupport) {
        augmented.push({
          number: midSupportPartNumber,
          type: 'Racking',
          description: midSupportDescription,
          price: midSupportPrice,
          category: 'Racking',
          quantity: totalModules,
          suggested_quantity: totalModules,
        });
      }
    }
  }

  const isGridFlex = isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId);
  if (isGridFlex) {
    const hasAttachments = augmented.some(p => p && ATTACHMENT_TYPES.has(p.type));
    if (hasAttachments) {
      const hwKitNumber = domesticContent ? '380930-US' : '380930';
      const hasHwKit = augmented.some(p => p && (p.number === '380930' || p.number === '380930-US'));
      if (!hasHwKit) {
        const gfxdHardwareKitQty = Math.ceil(totalModules / 10);
        augmented.push({
          number: hwKitNumber,
          type: 'Racking',
          description: 'GFXD HARDWARE KIT',
          price: 57.0,
          category: 'Racking',
          quantity: gfxdHardwareKitQty,
          suggested_quantity: gfxdHardwareKitQty,
        });
      }
    }
  }

  return augmented;
}

export function sortLiteBom(productId: number | undefined, partsList: BomPart[]): BomPart[] {
  if (productId === undefined || productId === null) return partsList;
  // Use shared legacy logic for consistent sorting
  if (isCommercialProduct(productId)) {
    return sortByUniracRMRequest(partsList as any);
  }
  return sortByUniracSMRequest(partsList as any);
}

